/**
 * 精灵图画廊弹窗
 * 支持多精灵图管理、缩略图网格浏览、一键切换、删除、重命名
 */
import { useRef, useCallback, useState } from 'react'
import { Modal, Button, Popconfirm, Typography, Skeleton, Input, App, Switch } from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleFilled,
  EditOutlined,
} from '@ant-design/icons'
import {
  DEFAULT_SPRITE_CONFIG,
  MAX_SPRITES,
  THUMBNAIL_SIZE,
  DEFAULT_SPRITE_ID,
} from './types'
import type { SpriteRecord } from './types'

const { Text } = Typography

interface SpriteGalleryModalProps {
  open: boolean
  onClose: () => void
  sprites: SpriteRecord[]
  activeId: string
  isLoading: boolean
  onAddSprite: (dataUrl: string, name?: string) => Promise<string>
  onRemoveSprite: (id: string) => Promise<void>
  onRenameSprite: (id: string, newName: string) => Promise<void>
  onSetActive: (id: string) => void
  onResetToDefault: () => void
  isDark?: boolean
  petEnabled?: boolean
  onPetEnabledChange?: (enabled: boolean) => void
}

const ACCEPT = '.jpg,.jpeg,.png,.webp'

const SpriteGalleryModal = ({
  open,
  onClose,
  sprites,
  activeId,
  isLoading,
  onAddSprite,
  onRemoveSprite,
  onRenameSprite,
  onSetActive,
  onResetToDefault: _onResetToDefault,
  isDark = false,
  petEnabled,
  onPetEnabledChange,
}: SpriteGalleryModalProps) => {
  const { message } = App.useApp()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')

  const isAtLimit = sprites.length >= MAX_SPRITES

  // 点击"+"卡片 → 直接打开文件选择器
  const handleAddClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // 文件选择后直接添加
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    e.target.value = ''

    if (file.size > 20 * 1024 * 1024) {
      message.error('文件大小不能超过 20MB')
      return
    }

    const reader = new FileReader()
    reader.onload = async (ev) => {
      const dataUrl = ev.target?.result as string
      if (!dataUrl) return
      try {
        await onAddSprite(dataUrl)
        message.success('精灵图添加成功')
      } catch {
        message.error('添加失败，请重试')
      }
    }
    reader.readAsDataURL(file)
  }, [onAddSprite, message])

  // 删除精灵图
  const handleDelete = async (id: string) => {
    try {
      await onRemoveSprite(id)
      message.success('已删除')
    } catch {
      message.error('删除失败')
    }
  }

  // 开始编辑名称
  const startEditing = (sprite: SpriteRecord) => {
    setEditingId(sprite.id)
    setEditingName(sprite.name)
  }

  // 确认重命名
  const confirmRename = () => {
    if (editingId && editingName.trim()) {
      onRenameSprite(editingId, editingName.trim())
    }
    setEditingId(null)
    setEditingName('')
  }

  // 取消编辑
  const cancelEditing = () => {
    setEditingId(null)
    setEditingName('')
  }

  // 生成精灵图首帧缩略图样式（CSS 精灵定位裁剪）
  const makeThumbnailStyle = (src: string) => ({
    width: THUMBNAIL_SIZE,
    height: THUMBNAIL_SIZE,
    backgroundImage: `url(${src})`,
    backgroundSize: `${THUMBNAIL_SIZE * DEFAULT_SPRITE_CONFIG.cols}px ${THUMBNAIL_SIZE * DEFAULT_SPRITE_CONFIG.rows}px`,
    backgroundPosition: '0 0',
    backgroundRepeat: 'no-repeat' as const,
    imageRendering: 'pixelated' as const,
  })

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 24 }}>
          <span>精灵图画廊</span>
          {onPetEnabledChange && (
            <Switch
              checked={petEnabled}
              onChange={onPetEnabledChange}
              size="small"
            />
          )}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={
        <div className="sprite-gallery-footer">
          <Text type="secondary" style={{ fontSize: 12 }}>
            {sprites.length}/{MAX_SPRITES} 张自定义精灵图
          </Text>
          <Button onClick={onClose}>关闭</Button>
        </div>
      }
      width={520}
      destroyOnHidden
      className={isDark ? 'dark' : ''}
    >
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : (
        <div className="sprite-gallery-grid">
          {/* 默认精灵图卡片 */}
          <div
            className={`sprite-gallery-card ${activeId === DEFAULT_SPRITE_ID ? 'active' : ''}`}
            onClick={() => onSetActive(DEFAULT_SPRITE_ID)}
            onKeyDown={(e) => e.key === 'Enter' && onSetActive(DEFAULT_SPRITE_ID)}
            tabIndex={0}
            role="button"
            aria-label="选择默认精灵图"
            aria-pressed={activeId === DEFAULT_SPRITE_ID}
          >
            {activeId === DEFAULT_SPRITE_ID && (
              <CheckCircleFilled className="sprite-gallery-check" />
            )}
            <div
              className="sprite-gallery-thumbnail"
              style={makeThumbnailStyle(DEFAULT_SPRITE_CONFIG.src)}
            />
            <span className="sprite-gallery-name">蜡笔小新</span>
          </div>

          {/* 用户精灵图卡片 */}
          {sprites.map((sprite) => (
            <div
              key={sprite.id}
              className={`sprite-gallery-card ${activeId === sprite.id ? 'active' : ''}`}
              onClick={() => onSetActive(sprite.id)}
              onKeyDown={(e) => e.key === 'Enter' && onSetActive(sprite.id)}
              tabIndex={0}
              role="button"
              aria-label={`选择精灵图: ${sprite.name}`}
              aria-pressed={activeId === sprite.id}
            >
              {activeId === sprite.id && (
                <CheckCircleFilled className="sprite-gallery-check" />
              )}
              <Popconfirm
                title="确定删除这张精灵图吗？"
                onConfirm={(e) => {
                  e?.stopPropagation()
                  handleDelete(sprite.id)
                }}
                onCancel={(e) => e?.stopPropagation()}
                okText="删除"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  className="sprite-gallery-delete"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`删除精灵图: ${sprite.name}`}
                  danger
                />
              </Popconfirm>
              <div
                className="sprite-gallery-thumbnail"
                style={makeThumbnailStyle(sprite.dataUrl)}
              />
              {editingId === sprite.id ? (
                <Input
                  size="small"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onPressEnter={confirmRename}
                  onBlur={confirmRename}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    e.stopPropagation()
                    if (e.key === 'Escape') cancelEditing()
                  }}
                  autoFocus
                  maxLength={20}
                  className="sprite-gallery-name-input"
                />
              ) : (
                <span className="sprite-gallery-name">
                  <span className="sprite-gallery-name-text">{sprite.name}</span>
                  <EditOutlined
                    className="sprite-gallery-edit-icon"
                    onClick={(e) => {
                      e.stopPropagation()
                      startEditing(sprite)
                    }}
                  />
                </span>
              )}
            </div>
          ))}

          {/* "+" 添加卡片 — 直接触发文件选择 */}
          {!isAtLimit && (
            <div
              className="sprite-gallery-add-card"
              onClick={handleAddClick}
              onKeyDown={(e) => e.key === 'Enter' && handleAddClick()}
              tabIndex={0}
              role="button"
              aria-label="添加新精灵图"
            >
              <PlusOutlined />
              <span>添加</span>
            </div>
          )}

          {/* 隐藏的文件选择器 */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      )}
    </Modal>
  )
}

export default SpriteGalleryModal
